/**
 * EditSiteModal - Modal for editing site details
 */

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface EditSiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: string;
  currentName: string;
  currentUrl: string;
  currentWpUsername: string | null;
}

interface TestConnectionResult {
  success: boolean;
  user?: string;
  siteName?: string;
  error?: string;
  message?: string;
}

export function EditSiteModal({
  isOpen,
  onClose,
  siteId,
  currentName,
  currentUrl,
  currentWpUsername,
}: EditSiteModalProps) {
  const queryClient = useQueryClient();

  // Form state
  const [name, setName] = useState(currentName);
  const [url, setUrl] = useState(currentUrl);
  const [wpUsername, setWpUsername] = useState(currentWpUsername || '');
  const [wpAppPassword, setWpAppPassword] = useState('');

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [testing, setTesting] = useState(false);

  // Reset form when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      setUrl(currentUrl);
      setWpUsername(currentWpUsername || '');
      setWpAppPassword('');
      setError(null);
      setTestResult(null);
    }
  }, [isOpen, currentName, currentUrl, currentWpUsername]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: {
      name?: string;
      url?: string;
      wp_username?: string;
      wp_app_password?: string;
    }) => {
      return api.patch<{ site: { id: string; name: string; url: string } }>(
        `/api/v1/sites/${siteId}`,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    },
  });

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setError(null);

    try {
      // If credentials changed, save them first
      if (wpAppPassword) {
        await api.patch(`/api/v1/sites/${siteId}`, {
          wp_username: wpUsername || undefined,
          wp_app_password: wpAppPassword,
        });
      }

      // Then test connection
      const result = await api.post<TestConnectionResult>(
        `/api/v1/sites/${siteId}/test-connection`,
        {}
      );
      setTestResult(result);
    } catch (err) {
      setTestResult({
        success: false,
        error: 'test_failed',
        message: err instanceof Error ? err.message : 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = () => {
    setError(null);

    // Build update payload (only include changed fields)
    const updates: Record<string, string> = {};

    if (name !== currentName) {
      updates.name = name;
    }
    if (url !== currentUrl) {
      updates.url = url;
    }
    if (wpUsername !== (currentWpUsername || '')) {
      updates.wp_username = wpUsername;
    }
    if (wpAppPassword) {
      updates.wp_app_password = wpAppPassword;
    }

    if (Object.keys(updates).length === 0) {
      onClose();
      return;
    }

    saveMutation.mutate(updates);
  };

  if (!isOpen) return null;

  const hasChanges =
    name !== currentName ||
    url !== currentUrl ||
    wpUsername !== (currentWpUsername || '') ||
    wpAppPassword !== '';

  const urlChanged = url !== currentUrl;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit Site</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Site Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Site Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="My WordPress Site"
            />
          </div>

          {/* Site URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Site URL
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://example.com"
            />
            {urlChanged && (
              <p className="mt-1 text-sm text-yellow-600">
                Changing the URL may require re-syncing content.
              </p>
            )}
          </div>

          {/* WordPress Credentials Section */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              WordPress Credentials
            </h3>

            {/* Username */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WordPress Username
              </label>
              <input
                type="text"
                value={wpUsername}
                onChange={(e) => setWpUsername(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="admin"
              />
            </div>

            {/* App Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Application Password
              </label>
              <input
                type="password"
                value={wpAppPassword}
                onChange={(e) => setWpAppPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Leave blank to keep current"
              />
              <p className="mt-1 text-xs text-gray-500">
                Generate an Application Password in WordPress under Users → Profile → Application Passwords
              </p>
            </div>

            {/* Test Connection Button */}
            <div className="mt-3">
              <button
                onClick={handleTestConnection}
                disabled={testing || !wpUsername}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg className={`w-4 h-4 ${testing ? 'animate-pulse' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {testing ? 'Testing...' : 'Test Connection'}
              </button>

              {/* Test result */}
              {testResult && (
                <div className={`mt-2 px-3 py-2 rounded-lg text-sm ${
                  testResult.success
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}>
                  {testResult.success
                    ? `Connected as ${testResult.user}${testResult.siteName ? ` to ${testResult.siteName}` : ''}`
                    : testResult.message || 'Connection failed'
                  }
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending || !hasChanges}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditSiteModal;
